import { useQuery } from '@tanstack/react-query'

import { type AppTimezonePreference, getTimezonePreferenceQueryKeyPart } from '@sparkai/utils'

import { apiClient } from '../../apiClient'
import { getSelectedTimezoneHeaders } from '../../timezoneRequestContext'
import {
  trainingApiPrefix,
  trainingQueryKey,
  type TrainingApiTarget,
} from '../../training-modules/trainingModuleApi'

export type PlanCapacityMetrics = {
  current?: {
    avgTtcTraining?: number | null
    avgTtcEngagement?: number | null
    avgAccuracy?: number | null
    passRate?: number | null
  }
  trend?: {
    avgTtcTraining?: number
    avgTtcEngagement?: number
    avgAccuracy?: number
    passRate?: number
  }
}

export const PROGRAM_PLAN_CAPACITY_METRICS_QUERY_KEY = 'program-plan-capacity-metrics'

type GetMetricsParams = {
  programId?: string
  timezonePreference?: AppTimezonePreference
  target?: TrainingApiTarget
}

function normalizeGetMetricsParams(params?: string | GetMetricsParams): GetMetricsParams {
  if (typeof params === 'string' || params === undefined) return { programId: params }
  return params
}

function getMetricsQueryKey(params: GetMetricsParams) {
  const key = trainingQueryKey(
    PROGRAM_PLAN_CAPACITY_METRICS_QUERY_KEY,
    params.programId,
    params.target ?? 'legacy'
  )
  return params.timezonePreference
    ? [...key, getTimezonePreferenceQueryKeyPart(params.timezonePreference)]
    : key
}

function getMetricsRequestConfig(params: GetMetricsParams) {
  const headers = params.timezonePreference ? getSelectedTimezoneHeaders(params.timezonePreference) : {}
  return { headers }
}

export function useGetMetrics(params?: string | GetMetricsParams) {
  const normalizedParams = normalizeGetMetricsParams(params)

  return useQuery({
    queryKey: getMetricsQueryKey(normalizedParams),
    queryFn: async () => {
      if (!normalizedParams.programId) throw new Error('Training program is not available')
      const url = `${trainingApiPrefix(normalizedParams.target ?? 'legacy')}/programs/${normalizedParams.programId}/training-modules/metrics`
      const requestConfig = getMetricsRequestConfig(normalizedParams)
      const response = Object.keys(requestConfig.headers).length
        ? await apiClient.get<PlanCapacityMetrics>(url, requestConfig)
        : await apiClient.get<PlanCapacityMetrics>(url)
      return response.data
    },
    enabled:
      Boolean(normalizedParams.programId) &&
      (normalizedParams.target === undefined ||
        normalizedParams.target === 'legacy' ||
        normalizedParams.target === 'modern'),
    refetchOnWindowFocus: false,
  })
}
